import { lazy, Suspense, useCallback, useEffect, useMemo, useState } from "react";
import { Navigate, Outlet, useLocation, useParams } from "react-router-dom";
import { isModuleEnabled, isModuleVisible, MODULES_CATALOG } from "@nxvolt/shared";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { useAuth } from "../lib/auth-context.js";
import { Sidebar } from "../components/Sidebar.js";
import { Topbar } from "../components/Topbar.js";
import { Skeleton } from "../components/Skeleton.js";
import { ModulePlaceholder } from "./ModulePlaceholder.js";
import { DashboardPage } from "./DashboardPage.js";
import { ChargersPage } from "./ChargersPage.js";
import { SessionsPage } from "./SessionsPage.js";
import { ChangePasswordPage } from "./ChangePasswordPage.js";
import { UsersPage } from "./UsersPage.js";
import { AlertsPage } from "./AlertsPage.js";
import { SettingsPage } from "./SettingsPage.js";
import { ChargerDetailPage } from "./ChargerDetailPage.js";
import { FeatureFlagsPage } from "./FeatureFlagsPage.js";
import {
  ChargerApprovalPage,
  ChargingWallPage,
  MaintenancePage,
  LivePage,
  ChargePhasesPage,
  OcppLogsPage,
  TelemetriaSubPage,
  ApiAccessPage,
  FleetPage,
  DriversPage,
  FinancialPage,
  PlanoContratadoPage,
  HorarioPontaPage,
  SystemLogsPage,
} from "./modules-extra.js";
import { TelemetriaFrotaPage } from "./TelemetriaFrotaPage.js";
import { MapaFrotaPage } from "./MapaFrotaPage.js";
import { OrphanSessionsPage } from "./OrphanSessionsPage.js";
import { CcoPage } from "./CcoPage.js";

const EnergiaPage = lazy(() => import("./EnergiaPage.js"));
const HeatmapUtilizacaoPage = lazy(() => import("./HeatmapUtilizacaoPage.js"));
const SaudeBateriaPage = lazy(() => import("./SaudeBateriaPage.js"));
const FleetPerformancePage = lazy(() => import("./FleetPerformancePage.js"));
const ManobristaPage = lazy(() => import("./ManobristaPage.js"));

/** Chave em localStorage para persistir estado colapsado. Constante nomeada
 *  para evitar typo silencioso — "1" = colapsada, ausente/"0" = expandida. */
const COLLAPSED_KEY = "nx.sidebar.collapsed";

/** Lê o estado inicial da sidebar do localStorage. Executado ANTES do primeiro
 *  paint (init lazy do useState) pra evitar flicker de expandida→colapsada.
 *  Precisa proteger contra SSR e navegadores que rejeitam localStorage. */
function readCollapsedInit(): boolean {
  if (typeof window === "undefined") return false;
  try {
    return window.localStorage.getItem(COLLAPSED_KEY) === "1";
  } catch {
    return false;
  }
}

/** Fallback usado dentro do <Suspense> das páginas lazy-loaded. Precisa ser
 *  ~do tamanho do <main> pra não induzir layout shift quando o bundle chega. */
function LazyRouteFallback() {
  return (
    <div style={{ padding: "var(--nx-space-6)" }}>
      <Skeleton variant="row" count={6} />
    </div>
  );
}

/** Cliente único do TanStack Query. Criado uma vez (useMemo) e mantido pelo
 *  ciclo de vida do AppShell — evita reset da cache a cada re-render.
 *  `staleTime` moderado (10s) para o CCO/telemetria: dado vira "stale"
 *  rápido mas revalidação é barata. */
function useAppQueryClient(): QueryClient {
  return useMemo(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            staleTime: 10_000,
            gcTime: 5 * 60_000,
            retry: 1,
            refetchOnWindowFocus: false,
          },
        },
      }),
    [],
  );
}

/** Layout do app autenticado. Rotas filhas recebem <Outlet />. */
export function AppShell() {
  const { user } = useAuth();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const queryClient = useAppQueryClient();
  // Init lazy: função só corre uma vez, no primeiro render. Sem isso ficaria
  // "expandido -> colapsado" no primeiro paint (usuário vê o menu piscar).
  const [collapsed, setCollapsed] = useState<boolean>(readCollapsedInit);

  // Fecha o drawer sempre que a rota muda. Sem isso, clicar num item do
  // menu mostrava a nova tela mas o drawer continuava por cima.
  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  // ESC fecha o drawer — mesma expectativa de modal. Só instalamos o
  // listener enquanto o drawer está aberto pra evitar handler ocioso.
  useEffect(() => {
    if (!menuOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setMenuOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [menuOpen]);

  // Persistir toda vez que muda. Try/catch pra Safari em modo privado
  // (localStorage lança QuotaExceededError).
  useEffect(() => {
    try {
      window.localStorage.setItem(COLLAPSED_KEY, collapsed ? "1" : "0");
    } catch {
      /* ignora — silenciar erro de armazenamento é aceitável */
    }
  }, [collapsed]);

  const toggleMenu = useCallback(() => setMenuOpen((v) => !v), []);
  const closeMenu = useCallback(() => setMenuOpen(false), []);
  const toggleCollapsed = useCallback(() => setCollapsed((v) => !v), []);

  if (!user) return null;

  return (
    <QueryClientProvider client={queryClient}>
      <div
        className={"nx-app" + (collapsed ? " nx-app--sidebar-collapsed" : "")}
      >
        <Topbar onToggleMenu={toggleMenu} menuOpen={menuOpen} />
        <div className="nx-app__body">
          {/* Sidebar desktop — o CSS esconde abaixo de 720px. */}
          <Sidebar
            collapsed={collapsed}
            onToggleCollapsed={toggleCollapsed}
          />
          {/* Drawer mobile — mesmo conteúdo, só aparece no CSS abaixo de 720px.
              Colapso não se aplica ao drawer; o Sidebar já ignora quando
              drawer=true. */}
          <Sidebar drawer open={menuOpen} onNavigate={closeMenu} />
          {/* Backdrop clicável fecha o drawer. */}
          <div
            className="nx-drawer-backdrop"
            data-open={menuOpen ? "true" : "false"}
            onClick={closeMenu}
            aria-hidden="true"
          />
          <main className="nx-app__main">
            {/* Suspense wrapper cobre TODAS as rotas filhas — as páginas
                lazy-loaded (Cco/Energia/Heatmap/SaudeBateria) sinalizam
                loading via este fallback. */}
            <Suspense fallback={<LazyRouteFallback />}>
              <Outlet />
            </Suspense>
          </main>
        </div>
      </div>
    </QueryClientProvider>
  );
}

/** Página de módulo — usa o slug da URL e verifica permissão. */
export function ModuleRoute({
  chargerDetail,
}: { chargerDetail?: boolean } = {}) {
  const params = useParams<{ slug?: string; cpId?: string }>();
  const slug = chargerDetail ? "chargers" : (params.slug ?? "");
  const { user } = useAuth();
  if (!user) return null;

  // change-password é sempre acessível ao próprio usuário
  if (slug === "change-password") return <ChangePasswordPage />;

  // Alias 90d — ADR 0002 §11: `battery_health` foi renomeado para
  // `saude_bateria`. Bookmarks antigos (com hífen ou underscore) redirecionam
  // sem quebrar. Remover após 2026-12-19.
  if (slug === "battery-health" || slug === "battery_health") {
    return <Navigate to="/app/saude-bateria" replace />;
  }
  // O router usa hífen na URL (`/app/saude-bateria`) e underscore no slug do
  // catálogo (`saude_bateria`). Normalizamos aqui para permitir ambos.
  const canonicalSlug = slug === "saude-bateria" ? "saude_bateria" : slug;

  const def = MODULES_CATALOG.find((m) => m.slug === canonicalSlug);
  if (!def) return <Navigate to="/app" replace />;
  if (!isModuleEnabled(canonicalSlug)) return <Navigate to="/app" replace />;
  if (!isModuleVisible(canonicalSlug)) return <Navigate to="/app" replace />;

  const canAccess = user.role === "admin" || user.modules.includes(canonicalSlug);
  if (!canAccess) {
    const first = user.modules[0];
    return <Navigate to={first ? `/app/${first}` : "/login"} replace />;
  }

  if (chargerDetail && params.cpId)
    return <ChargerDetailPage cpId={params.cpId} />;

  switch (canonicalSlug) {
    case "dashboard":
      return <DashboardPage />;
    case "chargers":
      return <ChargersPage />;
    case "sessions":
      return <SessionsPage />;
    case "alerts":
      return <AlertsPage />;
    case "users":
      return <UsersPage />;
    case "settings":
      return <SettingsPage />;
    case "charger_approval":
      return <ChargerApprovalPage />;
    case "charging_wall":
      return <ChargingWallPage />;
    case "cco":
      // Nova landing operacional — página consolidada dos 4 quadrantes.
      // Consome /api/cco/overview via TanStack Query (mock enquanto
      // backend-a não terminou a rota agregadora).
      return <CcoPage />;
    case "saude_bateria":
      return <SaudeBateriaPage />;
    case "energia_demanda":
      return <EnergiaPage />;
    case "heatmap_utilizacao":
      return <HeatmapUtilizacaoPage />;
    case "maintenance":
      return <MaintenancePage />;
    case "manobrista":
      return <ManobristaPage />;
    case "live":
      return <LivePage />;
    case "charge_phases":
      return <ChargePhasesPage />;
    case "ocpp_logs":
      return <OcppLogsPage />;
    case "telemetria_sub":
      return <TelemetriaSubPage />;
    case "api_access":
      return <ApiAccessPage />;
    case "fleet":
      return <FleetPage />;
    case "drivers":
      return <DriversPage />;
    case "telemetria_frota":
      return <TelemetriaFrotaPage />;
    case "mapa_frota":
      return <MapaFrotaPage />;
    case "orphan_sessions":
      return <OrphanSessionsPage />;
    case "financial":
      return <FinancialPage />;
    case "plano_contratado":
      return <PlanoContratadoPage />;
    case "rel_consumo":
      return <FleetPerformancePage />;
    case "horario_ponta":
      return <HorarioPontaPage />;
    case "system_logs":
      return <SystemLogsPage />;
    case "feature_flags":
      // Gate extra: só admin. Backend também recusa (403), mas evitamos
      // um round-trip para não-admin que tentou digitar a URL na barra.
      if (user.role !== "admin") {
        const first = user.modules[0];
        return <Navigate to={first ? `/app/${first}` : "/login"} replace />;
      }
      return <FeatureFlagsPage />;
    default:
      return <ModulePlaceholder slug={slug} />;
  }
}

/** Index de /app — redireciona para a landing preferida do usuário.
 *
 *  Precedência (ADR 0002 §11): CCO é a nova visão executiva, então se o
 *  usuário tem o módulo `cco` (grant admin/supervisor/operator/viewer)
 *  cai direto lá. Caso contrário, admin volta pro dashboard tradicional
 *  e demais usuários pro primeiro módulo do catálogo.
 */
export function AppIndexRedirect() {
  const { user } = useAuth();
  if (!user) return null;

  // Só redireciona para /app/cco se o slug `cco` já foi seedado pelo
  // backend (backend-a landa o slug no MODULES_CATALOG). Antes disso o
  // ModuleRoute jogaria pro /app de volta e criaria loop.
  const ccoInCatalog = MODULES_CATALOG.some((m) => m.slug === "cco");
  const hasCco =
    ccoInCatalog && (user.role === "admin" || user.modules.includes("cco"));
  if (hasCco) return <Navigate to="/app/cco" replace />;

  const first =
    user.role === "admin"
      ? "dashboard"
      : MODULES_CATALOG.find(
          (m) => isModuleEnabled(m.slug) && isModuleVisible(m.slug) && user.modules.includes(m.slug),
        )?.slug;
  return <Navigate to={first ? `/app/${first}` : "/login"} replace />;
}
